import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { apiService, AffiliateProgramPayload, Product } from '../services/api';

interface AffiliateProgramCreateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const AffiliateProgramCreateModal: React.FC<AffiliateProgramCreateModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    commissionType: 'percentage',
    commissionValue: '0',
    startDate: '',
    endDate: '',
    isActive: true,
  });
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchProducts();
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({ ...prev, startDate: today }));
    }
  }, [visible]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await apiService.getProducts({ limit: 100, includeDeleted: 'false' });
      setProducts(response.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const searchLower = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(searchLower) ||
      product.slug?.toLowerCase().includes(searchLower)
    );
  }).filter(product => !selectedProducts.find(p => p.id === product.id));

  const handleAddProduct = (product: Product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts([...selectedProducts, product]);
      setProductSearchTerm('');
      setShowProductDropdown(false);
    }
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.commissionValue) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.commissionType === 'percentage' && (parseFloat(formData.commissionValue) < 0 || parseFloat(formData.commissionValue) > 100)) {
      alert('Commission value for percentage must be between 0 and 100');
      return;
    }

    try {
      setLoading(true);

      const payload: AffiliateProgramPayload = {
        name: formData.name,
        commissionType: formData.commissionType,
        commissionValue: parseFloat(formData.commissionValue),
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        isActive: formData.isActive,
        products: selectedProducts.map(p => ({ productId: p.id })),
      };

      await apiService.createAffiliateProgram(payload);
      resetForm();
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create affiliate program:', error);
      alert(error.message || 'Failed to create affiliate program');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      name: '',
      commissionType: 'percentage',
      commissionValue: '0',
      startDate: today,
      endDate: '',
      isActive: true,
    });
    setSelectedProducts([]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">Create Affiliate Program</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              />
            </div>

            {/* Commission Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.commissionType}
                onChange={(e) => setFormData(prev => ({ ...prev, commissionType: e.target.value }))}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            {/* Commission Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission Value <span className="text-red-500">*</span>
                {formData.commissionType === 'percentage' && <span className="text-gray-500 text-xs ml-2">(0-100%)</span>}
              </label>
              <input
                type="number"
                value={formData.commissionValue}
                onChange={(e) => setFormData(prev => ({ ...prev, commissionValue: e.target.value }))}
                min="0"
                max={formData.commissionType === 'percentage' ? '100' : undefined}
                step="0.01"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  min={formData.startDate}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                />
              </div>
            </div>

            {/* Products Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Products
              </label>
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={productSearchTerm}
                    onChange={(e) => {
                      setProductSearchTerm(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary"
                    disabled={loadingProducts}
                  />
                </div>
                {showProductDropdown && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowProductDropdown(false)} />
                    <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No products found</div>
                      ) : (
                        filteredProducts.map(product => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleAddProduct(product)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                          >
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-xs text-gray-500">{product.slug}</div>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              {selectedProducts.length > 0 && (
                <div className="mt-2 space-y-2">
                  {selectedProducts.map(product => (
                    <div key={product.id} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                      <span className="text-sm text-gray-900">{product.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveProduct(product.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <XCircleIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Is Active */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-gray-300 text-imola-primary focus:ring-imola-primary"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Program'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AffiliateProgramCreateModal;

